import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Spin, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { 
  CheckCircleOutlined, 
  ClockCircleOutlined, 
  ShoppingCartOutlined,
  DollarOutlined,
  FileTextOutlined
} from '@ant-design/icons';
import { useSettings } from '../hooks/useSettings';
import '../styles/DashboardPage.css';

interface MetricCardData {
  title: string;
  value: number;
  icon: React.ReactNode;
  color: string;
  onClick: () => void;
}

interface DoctorStats {
  key: string;
  doctorName: string;
  totalPatients: number;
  activeOrders: number;
  totalOrderValue: number;
  completedOrders: number;
  readyToOrder: number;
}

export const DashboardPage: React.FC = () => {
  const { settings } = useSettings();
  const navigate = useNavigate();
  const isDarkMode = settings?.theme === 'dark';

  // State for metric cards
  const [readyToOrderCount, setReadyToOrderCount] = useState<number>(0);
  const [pendingPatientsCount, setPendingPatientsCount] = useState<number>(0);
  const [activeOrdersCount, setActiveOrdersCount] = useState<number>(0);
  const [pendingInvoiceBalance, setPendingInvoiceBalance] = useState<number>(0);
  
  // State for order value data
  const [orderValueData, setOrderValueData] = useState<{
    totalOrderValue: number;
    averageOrderValue: number;
    orderCount: number;
    doctorStats: Array<{
      doctorName: string;
      totalOrderValue: number;
      orderCount: number;
      averageOrderValue: number;
    }>;
  } | null>(null);

  // State for doctor stats
  const [doctorStats, setDoctorStats] = useState<DoctorStats[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [metricsLoading, setMetricsLoading] = useState<boolean>(true);

  // Fetch metric cards data
  useEffect(() => {
    const fetchMetrics = async () => {
      setMetricsLoading(true);
      try {
        // Create headers with cache-busting for fresh data
        const headers = {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache',
          'Content-Type': 'application/json'
        };

        // PERFORMANCE OPTIMIZATION: Fetch all metrics in parallel using Promise.all
        const [statsRes, orderStatsRes, orderValueRes, invoiceBalanceRes] = await Promise.all([
          fetch('/api/demographics/stats', { 
            headers,
            credentials: 'include' // Send HTTP-only authentication cookie
          }),
          fetch('/api/orders/stats', { 
            headers,
            credentials: 'include' // Send HTTP-only authentication cookie
          }),
          fetch('/api/orders/value-stats', { 
            headers,
            credentials: 'include' // Send HTTP-only authentication cookie
          }),
          fetch('/api/invoices/stats/pending-balance', { 
            headers,
            credentials: 'include' // Send HTTP-only authentication cookie
          })
        ]);

        // Process Pending Patients count
        if (statsRes.ok || statsRes.status === 304) {
          const data = await statsRes.json();
          setPendingPatientsCount(data.data.Pending || 0);
        }

        // Process Order stats (includes Ready to Order and Active Orders)
        if (orderStatsRes.ok || orderStatsRes.status === 304) {
          const data = await orderStatsRes.json();
          const stats = data.data;
          
          // FIX: Use correct "Ready to Order" count (excludes patients with existing orders)
          setReadyToOrderCount(stats['Ready to Order'] || 0);
          
          // FIX: Use pre-calculated "In Progress" count (includes all 4 statuses: InProgress, YetToBeShipped, ReadyToBeShipped, Shipped)
          setActiveOrdersCount(stats['Ready to Bill'] || 0);
        }

        // Process Order value stats
        if (orderValueRes.ok || orderValueRes.status === 304) {
          const data = await orderValueRes.json();
          setOrderValueData(data.data);
        }

        // Process Pending Invoice Balance
        if (invoiceBalanceRes.ok || invoiceBalanceRes.status === 304) {
          const data = await invoiceBalanceRes.json();
          setPendingInvoiceBalance(data.data?.totalPendingBalance || 0);
        }

      } catch (error) {
        console.error('❌ Error fetching metrics:', error);
        console.error('Error details:', error instanceof Error ? error.message : 'Unknown error');
        message.error('Failed to load dashboard metrics');
      } finally {
        console.log('✅ Metrics loading completed');
        setMetricsLoading(false);
      }
    };

    fetchMetrics();
  }, []);

  // Fetch doctor-wise statistics
  useEffect(() => {
    const fetchDoctorStats = async () => {
      setLoading(true);
      try {
        // Create headers with cache-busting for fresh data
        const headers = {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache',
          'Content-Type': 'application/json'
        };

        // PERFORMANCE OPTIMIZATION: Fetch both demographics and orders in parallel
        const [demographicsRes, ordersRes] = await Promise.all([
          fetch('/api/demographics?limit=10000', { 
            headers,
            credentials: 'include' // Send HTTP-only authentication cookie
          }),
          fetch('/api/orders?limit=10000', { 
            headers,
            credentials: 'include' // Send HTTP-only authentication cookie
          })
        ]);

        // Check if responses are valid (ok or cached)
        const demographicsOk = demographicsRes.ok || demographicsRes.status === 304;
        const ordersOk = ordersRes.ok || ordersRes.status === 304;

        if (!demographicsOk || !ordersOk) {
          throw new Error('Failed to fetch data');
        }

        const demographicsData = await demographicsRes.json();
        const ordersData = await ordersRes.json();

        const demographics = demographicsData.demographics || demographicsData.data || [];
        const orders = ordersData.data || [];

        // Aggregate data by doctor
        const doctorMap = new Map<string, DoctorStats>();

        // Track which demographics IDs already have orders (for Ready to Order calculation)
        const demographicsWithOrders = new Set<string>();
        orders.forEach((order: any) => {
          if (order.demographicsId) {
            demographicsWithOrders.add(order.demographicsId.toString());
          }
        });

        // Process demographics to count patients per doctor
        demographics.forEach((demo: any) => {
          if (!demo.doctorName) return;

          const doctorName = demo.doctorName;
          if (!doctorMap.has(doctorName)) {
            doctorMap.set(doctorName, {
              key: doctorName,
              doctorName,
              totalPatients: 0,
              activeOrders: 0,
              totalOrderValue: 0,
              completedOrders: 0,
              readyToOrder: 0
            });
          }

          const stats = doctorMap.get(doctorName)!;
          
          // Count total patients (excluding duplicates)
          if (!demo.isDuplicate) {
            stats.totalPatients++;
          }

          // Count ready to order patients (FIX: Exclude patients who already have orders, match metric card)
          if (demo.status === 'Accepted' && demo.surgeryDate && !demographicsWithOrders.has(demo.id?.toString())) {
            const surgeryDate = new Date(demo.surgeryDate);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            if (surgeryDate <= today) {
              stats.readyToOrder++;
            }
          }
        });

        // Process orders to calculate order counts and values
        orders.forEach((order: any) => {

          // Find the doctor from demographics
          const demo = demographics.find((d: any) => d.id?.toString() === order.demographicsId);
          if (!demo || !demo.doctorName) return;

          const doctorName = demo.doctorName;
          const stats = doctorMap.get(doctorName);
          if (!stats) return;

          // Count active orders (FIX: Include "Shipped" status to match metric card)
          const activeStatuses = ['InProgress', 'YetToBeShipped', 'ReadyToBeShipped', 'Shipped'];
          if (activeStatuses.includes(order.orderStatus)) {
            stats.activeOrders++;
          }

          // Count completed orders
          const completedStatuses = ['Completed', 'PaymentReceived'];
          if (completedStatuses.includes(order.orderStatus)) {
            stats.completedOrders++;
          }
        });

        // Update order values from API data if available
        if (orderValueData && orderValueData.doctorStats) {
          orderValueData.doctorStats.forEach(apiDoctorStats => {
            const stats = doctorMap.get(apiDoctorStats.doctorName);
            if (stats) {
              stats.totalOrderValue = apiDoctorStats.totalOrderValue;
            }
          });
        }

        // Convert map to array and sort by total patients
        const statsArray = Array.from(doctorMap.values()).sort(
          (a, b) => b.totalPatients - a.totalPatients
        );

        setDoctorStats(statsArray);
      } catch (error) {
        console.error('Error fetching doctor statistics:', error);
        message.error('Failed to load doctor statistics');
      } finally {
        setLoading(false);
      }
    };

    fetchDoctorStats();
  }, [orderValueData]);

  // Metric cards configuration
  const metricCards: MetricCardData[] = [
    {
      title: 'Ready to Order',
      value: readyToOrderCount,
      icon: <CheckCircleOutlined style={{ fontSize: '2rem', color: '#52c41a' }} />,
      color: '#52c41a',
      onClick: () => navigate('/orders')
    },
    {
      title: 'Pending IVR',
      value: pendingPatientsCount,
      icon: <ClockCircleOutlined style={{ fontSize: '2rem', color: '#faad14' }} />,
      color: '#faad14',
      onClick: () => navigate('/demographics', { state: { status: 'Pending' } })
    },
    {
      title: 'Ready To Bill',
      value: activeOrdersCount,
      icon: <ShoppingCartOutlined style={{ fontSize: '2rem', color: '#1890ff' }} />,
      color: '#1890ff',
      onClick: () => navigate('/orders', { state: { status: 'Ready to Bill' } })
    },
    {
      title: 'Total Order Value',
      value: orderValueData?.totalOrderValue || 0,
      icon: <DollarOutlined style={{ fontSize: '2rem', color: '#722ed1' }} />,
      color: '#722ed1',

    },
    {
      title: 'Pending Invoice Balance',
      value: pendingInvoiceBalance,
      icon: <FileTextOutlined style={{ fontSize: '2rem', color: '#eb2f96' }} />,
      color: '#eb2f96',
      onClick: () => navigate('/invoices')
    }
  ];

  // Table columns configuration
  const columns: ColumnsType<DoctorStats> = [
    {
      title: 'Doctor Name',
      dataIndex: 'doctorName',
      key: 'doctorName',
      sorter: (a, b) => a.doctorName.localeCompare(b.doctorName),
      render: (name: string) => (
        <p 
          // onClick={() => navigate(`/demographics?doctor=${encodeURIComponent(name)}`)}
          style={{ color: '#1890ff', fontWeight: 500 }}
        >
          {name}
        </p>
      ),
      width: '25%'
    },
    {
      title: 'Patients',
      dataIndex: 'totalPatients',
      key: 'totalPatients',
      sorter: (a, b) => a.totalPatients - b.totalPatients,
      align: 'center' as const,
      width: '12%'
    },
    {
      title: 'Active Orders',
      dataIndex: 'activeOrders',
      key: 'activeOrders',
      sorter: (a, b) => a.activeOrders - b.activeOrders,
      align: 'center' as const,
      width: '15%'
    },
    {
      title: 'Total Order Value',
      dataIndex: 'totalOrderValue',
      key: 'totalOrderValue',
      sorter: (a, b) => a.totalOrderValue - b.totalOrderValue,
      render: (value: number) => (
        <span style={{ fontWeight: 600, color: '#52c41a' }}>
          ${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </span>
      ),
      align: 'right' as const,
      width: '20%'
    },
    {
      title: 'Completed',
      dataIndex: 'completedOrders',
      key: 'completedOrders',
      sorter: (a, b) => a.completedOrders - b.completedOrders,
      align: 'center' as const,
      width: '13%'
    },
    {
      title: 'Ready to Order',
      dataIndex: 'readyToOrder',
      key: 'readyToOrder',
      sorter: (a, b) => a.readyToOrder - b.readyToOrder,
      align: 'center' as const,
      width: '15%'
    }
  ];

  return (
    <div className={`dashboard-page ${isDarkMode ? 'dark-mode' : ''}`}>
      <div className="dashboard-container">
        <h1>Dashboard</h1>
        <p>Welcome to your MPM Medical practitioner dashboard</p>
        
        {/* Metric Cards */}
        <div className="dashboard-stats">
          {metricsLoading ? (
            <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '2rem' }}>
              <Spin size="large" />
            </div>
          ) : (
            metricCards.map((card, index) => (
              <div 
                key={index} 
                className="stat-card" 
                onClick={card.onClick}
                style={{ cursor: 'pointer' }}
              >
                <div style={{ marginBottom: '1rem' }}>{card.icon}</div>
                <h3>{card.title}</h3>
                <p className="stat-number">
                  {card.title === 'Total Order Value' || card.title === 'Pending Invoice Balance'
                    ? `$${card.value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                    : card.value
                  }
                </p>
              </div>
            ))
          )}
        </div>
        
        {/* Doctor-wise Practice Overview Table */}
        <div className="dashboard-content">
          <h2 style={{ 
            color: isDarkMode ? '#ffffff' : '#2c3e50',
            fontSize: '1.5rem',
            fontWeight: 600,
            marginBottom: '1.5rem',
            paddingBottom: '0.5rem',
            borderBottom: isDarkMode ? '2px solid #334155' : '2px solid #ecf0f1'
          }}>
            Doctor-wise Practice Overview
          </h2>
          
          <Table
            columns={columns}
            dataSource={doctorStats}
            loading={loading}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} doctors`
            }}
            scroll={{ x: 'max-content' }}
            locale={{
              emptyText: 'No doctor data available'
            }}
            style={{
              background: isDarkMode ? '#1e293b' : 'white'
            }}
          />
        </div>
      </div>
    </div>
  );
};
